import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { DocumentPage } from 'Components/DocumentPage';
import { PDFDocument } from 'Components/PDFDocument';
import { RequisiteSelectModal } from 'Components/RequisiteComponents';
import UISpinner from 'Components/UIComponents/UISpinner';
import { useBooleanRefState, useModal, useRefState } from 'Hooks/Common';
import {
  useDocumentUpdate,
  useDocumentUpdateCancel,
  useInteractMedia,
} from 'Hooks/Document';
import {
  useDocumentFieldCreate,
  useDocumentFieldDelete,
  useDocumentFieldHistory,
  useDocumentFieldUpdate,
  useDocumentFieldUpdateLocally,
} from 'Hooks/DocumentFields';
import { useFieldShape, useInteractLeaveModal } from 'Hooks/Interact';
import interact from 'interactjs';
import { DocumentTypes, Signer } from 'Interfaces/Document';
import {
  DocumentField,
  DocumentFieldAction,
  DocumentFieldActions,
  DocumentFieldAddType,
  DocumentFieldsCRUDMeta,
  DocumentFieldTypes,
  DocumentFieldUpdatePayload,
  FieldCreateType,
} from 'Interfaces/DocumentFields';
import {
  defaultRequisiteTypesOrder,
  Requisite,
  RequisiteType,
} from 'Interfaces/Requisite';
import { User } from 'Interfaces/User';
import { flatten, isEmpty, minBy, throttle } from 'lodash-es';
import { fontFamilyByValue, interactInitialFont } from 'Services/Fonts';
import Interact from 'Services/Interact';
import Toast from 'Services/Toast';
import { rootActions } from 'Store';
import { checkIfDateOrText, getCurrentDate, isRequisite } from 'Utils/functions';
import {
  selectAvailableSignatureTypes,
  selectDocument,
  selectDocumentFields,
  selectDocumentLastInteractedField,
  selectDocumentPdfMetadataFromDocumentPart,
  selectDocumentSigner,
  selectDocumentSignerOptions,
  selectInteractDesktopGreaterPageWidth,
  selectInteractLoadedPagesCount,
  selectInteractPagesPerDocument,
  selectInteractPdfDocumentFiles,
  selectInteractTotalPagesCount,
  selectIsAllInteractPagesRendered,
  selectIsAllInteractPdfFilesLoaded,
  selectUser,
} from 'Utils/selectors';
import uuid from 'uuid/v4';

import { INTERACT_SIZES } from '../../../common/constants';
import { defaultSizesByFieldType } from '../../../common/fieldShapes';
import { InteractModalOldProps } from '../../../common/interfaces';
import { createPitchToZoom } from '../../../common/pitchToZoom';
import {
  calculateSizeRatio,
  changeFieldDropDownPositionInvalidity,
  updateScrollOnScale,
} from '../../../common/utils';
import FieldItemOptions from '../../../components/FieldItemOptions';
import InteractFieldBar from '../../../components/InteractFieldBar';
import InteractNavigation from '../../../components/InteractNavigation';
import MeOptionModal from '../../../components/MeOptionModal';
import ModalHeader from '../../../components/ModalHeader';
import { FieldItem } from '../../FieldItem';

function InteractModalOldTablet({
  documentId,
  onClose,
  handleSubmit,
  submitting,
  buttonSendTitle,
  isDocumentInteractLoading,
}: InteractModalOldProps) {
  const documentIdObject = useMemo(() => ({ documentId }), [documentId]);

  const [isMedium, isTablet] = useInteractMedia(['md', 'lg']);
  const currentDocument = useSelector(state => selectDocument(state, documentIdObject));

  const pdfPageMeta = useSelector(state =>
    selectDocumentPdfMetadataFromDocumentPart(state, documentIdObject),
  );

  const pdfDocumentFiles = useSelector(state =>
    selectInteractPdfDocumentFiles(state, documentIdObject),
  );

  const pagesPerDocument = useSelector(state =>
    selectInteractPagesPerDocument(state, documentIdObject),
  );

  const totalPagesCount = useSelector(state =>
    selectInteractTotalPagesCount(state, documentIdObject),
  );

  const isAllPdfFilesLoaded = useSelector(state =>
    selectIsAllInteractPdfFilesLoaded(state, documentIdObject),
  );

  const loadedPagesCount = useSelector(state =>
    selectInteractLoadedPagesCount(state, documentIdObject),
  );

  const isAllPagesRendered = useSelector(state =>
    selectIsAllInteractPagesRendered(state, documentIdObject),
  );

  const greaterPageWidth = useSelector(state =>
    selectInteractDesktopGreaterPageWidth(state, documentIdObject),
  );

  const dispatch = useDispatch();

  const [
    redoDocumentFieldAction,
    undoDocumentFieldAction,
    isNextAvailable,
    isPrevAvailable,
  ] = useDocumentFieldHistory();

  const documentSignersSelectorProps = useMemo(
    () => ({
      documentId,
      isPreparerSigner: true,
    }),
    [documentId],
  );

  const signersOptions = useSelector(state =>
    selectDocumentSignerOptions(state, documentSignersSelectorProps),
  );

  const documentFields: DocumentField[] = useSelector(selectDocumentFields);

  const containerRef = useRef<HTMLDivElement>(null);
  const pagesContainerRef = useRef<HTMLDivElement>(null);
  const [
    documentViewContainer,
    setDocumentViewContainer,
  ] = useState<HTMLDivElement | null>(null);
  const clipboardRef = useRef<DocumentField | null>(null);
  const [enableResizeFont, setEnableResizeFont] = useState<boolean>(true);

  const [baseScale, setBaseScale] = useState<number>(1);
  const [selectedScale, setSelectedScale] = useState<number>(1);
  const documentScale = baseScale * selectedScale;

  const [selectedFieldType, setSelectedFieldType] = useState<
    DocumentFieldTypes | undefined
  >();
  const [lastInteractedFieldId, setLastInteractedFieldId] = useState<string | null>(null);
  const [currentFieldAction, setCurrentFieldAction] = useState<
    DocumentFieldAction<DocumentFieldActions> | undefined
  >();
  const [meOptionSelected, setMeOptionSelected] = useState(false);
  const [isFieldShapeExist, setFieldShapeExist] = useState<boolean>(false);
  const [generalPageArray, setGeneralPageArray] = useState<ChildNode[]>([]);

  const [openMeOptionModal, closeMeOptionModal] = useModal(() => (
    <MeOptionModal onClose={closeMeOptionModal} />
  ));

  const createDocumentField = useDocumentFieldCreate();
  const deleteDocumentField = useDocumentFieldDelete();
  const updateDocumentField = useDocumentFieldUpdate();
  const updateDocumentFieldLocally = useDocumentFieldUpdateLocally();
  const cancelDocumentUpdate = useDocumentUpdateCancel();
  const [updateDocument] = useDocumentUpdate();

  const {
    ref: isAbleToDeleteLastInteractedFieldRef,
    enableValue: enableToDeleteLastInteractedField,
    disableValue: disableToDeleteLastInteractedField,
  } = useBooleanRefState(true);

  const {
    ref: focusedFieldIdRef,
    setValue: setFocusedFieldId,
    clearValue: unsetFocusedFieldId,
  } = useRefState<string>();

  const {
    fieldRootRef,
    fieldShapeContainerRef,
    removeFieldShape,
    addInvalidityToFieldShape,
    removeInvalidityFromFieldShape,
  } = useFieldShape({
    isFieldShapeExist,
    onRemoveFieldShape: () => {
      setFieldShapeExist(false);
      setSelectedFieldType(undefined);
    },
  });

  const lastInteractedField = useSelector(state =>
    selectDocumentLastInteractedField(state, {
      ...documentSignersSelectorProps,
      lastInteractedFieldId,
    }),
  );
  const lastInteractedFieldSigner = useSelector(state =>
    selectDocumentSigner(state, {
      documentId,
      signerId: lastInteractedField?.signerId,
    }),
  );

  const { dateFormat, name: userName }: User = useSelector(selectUser);
  const availableSignatureTypes = useSelector(selectAvailableSignatureTypes);

  useEffect(() => {
    return () => {
      cancelDocumentUpdate();
    };
  }, [documentId, cancelDocumentUpdate]);

  useEffect(() => {
    dispatch(rootActions.documentInteract.resetPagesRendered(documentId));
  }, [dispatch, documentId]);

  const [, { resetIfBlocked }] = useInteractLeaveModal();

  const isPreparingInteract = loadedPagesCount !== totalPagesCount;
  const lowScale = selectedScale < 1;

  const getGeneralArrayOfPagesComponents = useCallback(() => {
    const fileNodes = pagesContainerRef.current?.childNodes;

    if (fileNodes) {
      const pageArray: ChildNode[] = [];

      fileNodes.forEach(child => {
        pageArray.push(...flatten(child.childNodes));
      });

      setGeneralPageArray(pageArray);

      return pageArray;
    }
  }, []);

  const getFieldPageComponent = useCallback(
    (pageNumber: DocumentField['pageNumber']) => {
      const pages = getGeneralArrayOfPagesComponents() as HTMLElement[];

      return pages[pageNumber - 1];
    },
    [getGeneralArrayOfPagesComponents],
  );

  const newFieldSigner = useMemo(
    () =>
      currentDocument?.signers.reduce((accumSigner, signer) => {
        if (lastInteractedFieldSigner && !lastInteractedFieldSigner.isPreparer) {
          return lastInteractedFieldSigner;
        }

        if (!accumSigner || accumSigner.isPreparer) return signer;

        return accumSigner;
      }, undefined as Signer | undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentDocument?.signers, lastInteractedFieldSigner],
  );

  const [openRequisiteSelectModal, closeRequisiteSelectModal, isModalOpen] = useModal(
    () => (
      <RequisiteSelectModal
        onClose={() => {
          setCurrentFieldAction(undefined);
          closeRequisiteSelectModal();
        }}
        type={
          currentFieldAction?.payload?.type === DocumentFieldTypes.Signature
            ? RequisiteType.SIGN
            : RequisiteType.INITIAL
        }
        availableSignatureTypes={
          currentFieldAction?.payload?.availableSignatureTypes ||
          defaultRequisiteTypesOrder
        }
        onSelect={(requisite: Requisite) => {
          if (!currentFieldAction) return;

          setCurrentFieldAction({
            ...currentFieldAction,
            payload: {
              ...currentFieldAction.payload,
              requisiteId: requisite.id,
            },
          });
          closeRequisiteSelectModal();
        }}
      />
    ),
    [currentFieldAction],
  );

  const setFieldCreateAction = useCallback(
    (fieldData: DocumentFieldAddType, meta?: DocumentFieldsCRUDMeta) => {
      const { type } = fieldData;
      const isDateOrText = checkIfDateOrText(type);
      const defaultSizes = defaultSizesByFieldType[type];
      const fieldPageComponent = getFieldPageComponent(fieldData.pageNumber);
      const fontStyles = isDateOrText ? interactInitialFont : undefined;
      const coordinateX =
        fieldData.coordinateX + defaultSizes.width > fieldPageComponent.clientWidth
          ? fieldData.coordinateX -
            (fieldData.coordinateX + defaultSizes.width - fieldPageComponent.clientWidth)
          : fieldData.coordinateX;
      const coordinateY =
        fieldData.coordinateY + defaultSizes.height > fieldPageComponent.clientHeight
          ? fieldData.coordinateY -
            (fieldData.coordinateY +
              defaultSizes.height -
              fieldPageComponent.clientHeight)
          : fieldData.coordinateY;
      const newField: DocumentField = {
        id: uuid(),
        documentId,
        dateFormat: fieldData.type === DocumentFieldTypes.Date ? dateFormat : null,
        requisiteId: null,
        availableSignatureTypes: isRequisite(fieldData.type)
          ? availableSignatureTypes
          : null,
        checked:
          fieldData.type === DocumentFieldTypes.Checkbox
            ? newFieldSigner?.isPreparer
            : null,
        ...fontStyles,
        ...defaultSizes,
        ...fieldData,
        text: newFieldSigner?.isPreparer
          ? fieldData.type === DocumentFieldTypes.Date
            ? getCurrentDate(dateFormat)
            : fieldData.type === DocumentFieldTypes.Name
            ? userName
            : undefined
          : undefined,
        signerId: newFieldSigner?.id || null,
        coordinateX,
        coordinateY,
        required: true,
        style: {
          maxWidth: fieldPageComponent.clientWidth,
          maxHeight: fieldPageComponent.clientHeight,
          width: defaultSizes.width,
          height: defaultSizes.height,
          fontSize: fontStyles?.fontSize,
          fontFamily: fontFamilyByValue[fontStyles?.fontFamily as string],
          ...fieldData.style,
          left: coordinateX + fieldPageComponent?.offsetLeft,
          top: coordinateY + fieldPageComponent?.offsetTop,
        },
      };

      setCurrentFieldAction({
        type: DocumentFieldActions.CREATE,
        payload: newField,
        meta,
      });
    },
    [
      getFieldPageComponent,
      documentId,
      dateFormat,
      availableSignatureTypes,
      newFieldSigner?.isPreparer,
      newFieldSigner?.id,
      userName,
    ],
  );

  const setFieldUpdateAction = useCallback(
    (payload: DocumentFieldUpdatePayload, meta?: DocumentFieldsCRUDMeta) => {
      const sizePayload = {};

      if (payload.width && payload.coordinateX && payload.pageNumber) {
        const fieldPageComponent = getFieldPageComponent(payload.pageNumber);
        const newWidth =
          payload.coordinateX + payload.width > fieldPageComponent.clientWidth
            ? fieldPageComponent.clientWidth - payload.coordinateX
            : payload.width;

        Object.assign(sizePayload, {
          width: newWidth,
          style: {
            ...payload.style,
            width: newWidth,
          },
        });
      }

      if (meta?.stopFontAutoSize) {
        setEnableResizeFont(false);
      }

      setCurrentFieldAction({
        type: DocumentFieldActions.UPDATE,
        payload: { ...payload, ...sizePayload },
        meta,
      });
    },
    [getFieldPageComponent],
  );

  const addField = useCallback(
    (action: DocumentFieldAction<DocumentFieldActions.CREATE>) => {
      createDocumentField(action.payload, action.meta);
      setLastInteractedFieldId(action.payload.id);
    },
    [createDocumentField],
  );

  const updateField = useCallback(
    (action: DocumentFieldAction<DocumentFieldActions.UPDATE>) => {
      updateDocumentField(action.payload, action.meta);
    },
    [updateDocumentField],
  );

  const executeCurrentFieldAction = useCallback(
    (action: DocumentFieldAction<DocumentFieldActions>) => {
      switch (action.type) {
        case DocumentFieldActions.CREATE: {
          return addField(action as DocumentFieldAction<DocumentFieldActions.CREATE>);
        }
        case DocumentFieldActions.UPDATE: {
          return updateField(action as DocumentFieldAction<DocumentFieldActions.UPDATE>);
        }
        default:
          return;
      }
    },
    [addField, updateField],
  );

  useEffect(() => {
    if (!currentFieldAction || !currentDocument) return;

    const fieldSigner = currentDocument?.signers.find(
      signer => signer.id === currentFieldAction.payload.signerId,
    );

    if (
      currentFieldAction.payload.type &&
      isRequisite(currentFieldAction.payload.type) &&
      fieldSigner?.isPreparer &&
      !currentFieldAction.payload.requisiteId
    ) {
      openRequisiteSelectModal();
      return;
    }

    setCurrentFieldAction(undefined);
    executeCurrentFieldAction(currentFieldAction);
  }, [
    currentFieldAction,
    openRequisiteSelectModal,
    currentDocument,
    executeCurrentFieldAction,
  ]);

  const recalculateDocumentFieldStyles = useCallback(
    (documentField: DocumentField) => {
      const pagesContainerComponent = pagesContainerRef.current as HTMLElement;
      const fieldPageComponent = getFieldPageComponent(documentField.pageNumber);

      if (!fieldPageComponent) return;

      const pageOffset =
        (greaterPageWidth - pdfPageMeta[documentField.pageNumber].width) / 2 || 0;

      const updatedField = {
        ...documentField,
        style: {
          ...documentField.style,
          width: documentField.width || documentField.style?.width,
          height: documentField.height || documentField.style?.height,
          maxWidth: fieldPageComponent.clientWidth,
          maxHeight: fieldPageComponent.clientHeight,
          left:
            documentField.coordinateX + pagesContainerComponent.offsetLeft + pageOffset,
          top: documentField.coordinateY + fieldPageComponent.offsetTop,
          fontFamily: documentField.fontFamily
            ? fontFamilyByValue[documentField.fontFamily]
            : undefined,
          fontSize: documentField.fontSize || undefined,
        },
      };

      updateDocumentFieldLocally(updatedField);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateDocumentFieldLocally, pagesContainerRef.current],
  );

  useEffect(() => {
    if (pagesContainerRef.current && isAllPdfFilesLoaded && isAllPagesRendered) {
      documentFields.forEach(recalculateDocumentFieldStyles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagesContainerRef.current, isAllPdfFilesLoaded, isAllPagesRendered]);

  const handleDocumentScaleChange = useCallback(
    (nextSelectedScale: number, anchor?: { clientX: number; clientY: number }) => {
      const currentDocumentScale = documentScale;
      const nextDocumentScale = baseScale * nextSelectedScale;

      updateScrollOnScale({
        element: documentViewContainer,
        anchor,
        documentScale: currentDocumentScale,
        nextDocumentScale,
      });

      documentFields.forEach(recalculateDocumentFieldStyles);

      setSelectedScale(nextSelectedScale);
    },
    [
      documentFields,
      baseScale,
      documentScale,
      recalculateDocumentFieldStyles,
      documentViewContainer,
    ],
  );

  useEffect(() => {
    const documentView = documentViewContainer;
    if (!documentView) return;

    const pitchToZoom = createPitchToZoom(documentView, {
      scale: selectedScale,
      onTouchMoveComplete: handleDocumentScaleChange,
    });
    pitchToZoom.attachListeners();
    return () => void pitchToZoom.detachListeners();
  }, [documentViewContainer, selectedScale, handleDocumentScaleChange]);

  const updateContainerBaseRatio = useCallback(
    (container: HTMLElement | null) => {
      const baseRatio = calculateSizeRatio(greaterPageWidth, container);
      if (typeof baseRatio !== 'undefined' && baseRatio > 0) {
        setBaseScale(baseRatio);
      }
    },
    [greaterPageWidth],
  );

  useEffect(() => {
    if (!documentViewContainer || isPreparingInteract) return;

    function resize() {
      updateContainerBaseRatio(documentViewContainer);
    }

    resize();
    const throttledResize = throttle(resize, 750);

    window.addEventListener('resize', throttledResize);
    return () => window.removeEventListener('resize', throttledResize);
  }, [documentViewContainer, isPreparingInteract, updateContainerBaseRatio]);

  const navigateToPage = useCallback(
    absolutePageNumber => {
      const targetPage = generalPageArray[absolutePageNumber - 1] as HTMLDivElement;

      if (!targetPage || !documentViewContainer) return;

      targetPage.scrollIntoView({ behavior: 'smooth' });
    },
    [generalPageArray, documentViewContainer],
  );

  const handleFieldDropOnPageContainer = useCallback(
    event => {
      changeFieldDropDownPositionInvalidity(event, false);
      const targetFieldNode = event.dragEvent.target as HTMLDivElement;
      const isClone = targetFieldNode.getAttribute('data-clone') === 'true';
      if (isClone) {
        return targetFieldNode.remove();
      }
      const targetFieldId = targetFieldNode.id;
      const targetDocumentField = documentFields.find(
        documentField => documentField.id === targetFieldId,
      ) as DocumentField;
      const targetFieldStyles = targetDocumentField?.style;
      if (targetFieldStyles?.left) {
        targetFieldNode.style.left = `${targetFieldStyles.left}px`;
        targetFieldNode.setAttribute('data-coord-x', targetFieldStyles.left.toString());
      }
      if (targetFieldStyles?.top) {
        targetFieldNode.style.top = `${targetFieldStyles.top}px`;
        targetFieldNode.setAttribute('data-coord-y', targetFieldStyles.top.toString());
      }
    },
    [documentFields],
  );

  const handleFieldDropOnPage = useCallback(
    (event, pageNumber: number) => {
      const { dragEvent, target: targetPage } = event;
      const targetFieldNode = dragEvent.target as HTMLElement;
      const fieldRect = interact.getElementRect(targetFieldNode);
      const dropzoneRect = interact.getElementRect(targetPage);
      const isClone = targetFieldNode.getAttribute('data-clone') === 'true';
      const offsetX = Math.abs(fieldRect.left - dropzoneRect.left) / documentScale;
      const offsetY = Math.abs(fieldRect.top - dropzoneRect.top) / documentScale;
      const positioning = {
        coordinateX: offsetX,
        coordinateY: offsetY,
        style: {
          left: offsetX + targetPage.offsetLeft,
          top: offsetY + targetPage.offsetTop,
        },
      };

      if (isClone && documentViewContainer) {
        const type = targetFieldNode.getAttribute(
          'data-field-type',
        ) as DocumentFieldTypes;

        setFieldCreateAction({
          signerId: null,
          required: false,
          type,
          pageNumber: pageNumber,
          ...positioning,
          createType: FieldCreateType.ADD,
        });

        return targetFieldNode.remove();
      }

      const targetFieldId = targetFieldNode.id;
      const targetField = documentFields.find(
        documentField => documentField.id === targetFieldId,
      ) as DocumentField;

      setFieldUpdateAction({
        ...targetField,
        pageNumber: pageNumber,
        ...positioning,
        style: {
          ...targetField.style,
          ...positioning.style,
        },
      });
    },
    [
      setFieldCreateAction,
      setFieldUpdateAction,
      documentFields,
      documentViewContainer,
      documentScale,
    ],
  );

  useEffect(() => {
    const draggableFieldItem = interact(`div[data-clone=true]`)
      .draggable({
        onmove: Interact.dragMoveListener,
      })
      .on('down', event => {
        const { interaction } = event;

        interaction.start({ name: 'drag' }, event.interactable, event.currentTarget);
      });

    return () => draggableFieldItem.unset();
  }, []);

  useEffect(() => {
    if (isAllPdfFilesLoaded && pagesContainerRef.current) {
      getGeneralArrayOfPagesComponents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllPdfFilesLoaded, pagesContainerRef.current, getGeneralArrayOfPagesComponents]);

  useEffect(() => {
    if (containerRef.current) {
      const listeners = interact(containerRef.current).dropzone({
        overlap: 0,
        ondragenter: event => changeFieldDropDownPositionInvalidity(event, true),
        ondragleave: event => changeFieldDropDownPositionInvalidity(event, false),
        ondrop: handleFieldDropOnPageContainer,
      });

      return () => listeners.unset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleFieldDropOnPageContainer, documentFields, containerRef.current]);

  const handleSignerChange = (signerId: string) => {
    signersOptions.forEach(signer => {
      if (signer.value === signerId && signer.label === 'Me (now)') {
        if (!meOptionSelected) {
          openMeOptionModal();
          setMeOptionSelected(true);
        }
      }
    });
  };

  const handleDocumentFieldsSubmit = useCallback(async () => {
    try {
      if (currentDocument) {
        const updateValues = {
          documentId: documentId,
          fields: documentFields,
          type: currentDocument.type,
        };

        await updateDocument({ values: updateValues, query: { log: true } });
        resetIfBlocked();
        await handleSubmit();

        Toast.success('Document successfully saved!');
      }
    } catch (error) {
      Toast.handleErrors(error);
    }
  }, [
    currentDocument,
    documentId,
    documentFields,
    updateDocument,
    resetIfBlocked,
    handleSubmit,
  ]);

  const handlePageTap = useCallback(
    (event, pageNumber: number) => {
      if (!isFieldShapeExist) {
        return;
      }
      setLastInteractedFieldId(null);

      if (selectedFieldType) {
        setFieldCreateAction({
          signerId: null,
          type: selectedFieldType,
          required: false,
          coordinateX: event.offsetX,
          coordinateY: event.offsetY,
          pageNumber: pageNumber,
          style: {
            left: event.offsetX + event.currentTarget.offsetLeft,
            top: event.offsetY + event.currentTarget.offsetTop,
          },
          createType: FieldCreateType.ADD,
        });
      }
    },
    [isFieldShapeExist, selectedFieldType, setFieldCreateAction],
  );

  const handlePageRendered = useCallback(
    (absolutePageNumber: number) => {
      dispatch(
        rootActions.documentInteract.setPageRendered({
          documentId,
          pageNumber: absolutePageNumber,
        }),
      );
    },
    [dispatch, documentId],
  );

  const copyDocumentField = useCallback(() => {
    clipboardRef.current =
      documentFields.find(documentField => documentField.id === lastInteractedFieldId) ||
      null;
  }, [documentFields, lastInteractedFieldId]);

  const deleteLastInteractedField = useCallback(() => {
    if (
      lastInteractedFieldSigner?.isPreparer &&
      focusedFieldIdRef.current === lastInteractedFieldId &&
      lastInteractedField?.type &&
      checkIfDateOrText(lastInteractedField.type)
    ) {
      return;
    }

    if (lastInteractedFieldId) {
      deleteDocumentField({ id: lastInteractedFieldId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deleteDocumentField,
    lastInteractedField,
    lastInteractedFieldId,
    lastInteractedFieldSigner,
  ]);

  const getPagesRects = useCallback(() => {
    if (!pagesContainerRef.current) return;

    return Array.from({ length: generalPageArray.length }, (_, index) =>
      interact.getElementRect(generalPageArray[index] as HTMLElement),
    );
  }, [generalPageArray]);

  const getDocumentViewCenter = useCallback(() => {
    if (!documentViewContainer) return;

    const documentViewRect = interact.getElementRect(documentViewContainer);
    return documentViewRect.top + documentViewRect.height / 2;
  }, [documentViewContainer]);

  const findPageClosestToCenter = useCallback(() => {
    const pagesRects = getPagesRects();
    if (!pagesRects || !pagesRects.length) return;

    const pagesCenters = pagesRects
      .map((pageRect, index) => ({
        index,
        center: pageRect !== undefined ? pageRect.top + pageRect.height / 2 : 0,
      }))
      .filter(page => page.center > 0);

    const documentViewCenter = getDocumentViewCenter() as number;

    return minBy(pagesCenters, page =>
      Math.abs(documentViewCenter - page.center),
    ) as typeof pagesCenters[0];
  }, [getPagesRects, getDocumentViewCenter]);

  const pasteDocumentField = useCallback(() => {
    if (clipboardRef.current && documentViewContainer && pagesContainerRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, ...fieldData } = clipboardRef.current;

      if (focusedFieldIdRef.current) return;

      const closestPage = findPageClosestToCenter();

      if (!closestPage) return;

      const pageComponent = getFieldPageComponent(closestPage.index + 1);

      const pageCenterX = pageComponent?.clientWidth
        ? pageComponent.clientWidth / 2 - (fieldData.width as number) / 2
        : 0;
      const pageCenterY = pageComponent?.clientHeight
        ? pageComponent.clientHeight / 2 - (fieldData.height as number) / 2
        : 0;

      setFieldCreateAction({
        ...fieldData,
        pageNumber: (closestPage?.index || 0) + 1,
        coordinateX: pageCenterX,
        coordinateY: pageCenterY,
        style: {
          ...fieldData.style,
          left: pageComponent?.offsetLeft + pageCenterX,
          top: pageComponent?.offsetTop + pageCenterY,
        },
        createType: FieldCreateType.COPY,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    documentViewContainer,
    pagesContainerRef.current,
    findPageClosestToCenter,
    getFieldPageComponent,
    setFieldCreateAction,
  ]);

  const handleKeydown = useCallback(
    (event: KeyboardEvent) => {
      if (isModalOpen) return;

      if (
        isAbleToDeleteLastInteractedFieldRef.current &&
        (event.key === 'Backspace' || event.key === 'Delete')
      ) {
        return deleteLastInteractedField();
      }

      if (event.metaKey || event.ctrlKey) {
        switch (event.key.toLocaleLowerCase()) {
          case 'c': {
            return copyDocumentField();
          }
          case 'v': {
            return pasteDocumentField();
          }
          case 'z': {
            return event.shiftKey ? redoDocumentFieldAction() : undoDocumentFieldAction();
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isModalOpen,
      copyDocumentField,
      deleteLastInteractedField,
      pasteDocumentField,
      redoDocumentFieldAction,
      undoDocumentFieldAction,
    ],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeydown);

    return () => window.removeEventListener('keydown', handleKeydown);
  }, [handleKeydown]);

  const validateTag = useCallback(
    (id: string, tag: string) =>
      tag ? documentFields.every(field => field.tag !== tag || field.id === id) : true,
    [documentFields],
  );

  const handleApplyFontToEachField = (fontFamily: string, fontSize: number) => {
    documentFields.forEach(field => {
      updateDocumentField(
        {
          ...field,
          fontFamily: fontFamily,
          fontSize: fontSize,
          style: {
            fontFamily: fontFamilyByValue[fontFamily],
            fontSize: fontSize,
          },
        },
        {
          pushToHistory: true,
        },
      );
    });
  };

  const handlePdfDocumentLoadSuccess = useCallback(
    (pdfDocumentFile: string, pdfDocumentProxy) => {
      dispatch(
        rootActions.documentInteract.setPagesForFile({
          documentId,
          pdfFile: pdfDocumentFile,
          pagesNumber: pdfDocumentProxy.numPages,
        }),
      );
    },
    [dispatch, documentId],
  );

  const getAbsolutePageNumber = useCallback(
    (pdfDocumentFileIdx: number, pageNumber: number) =>
      pdfDocumentFiles
        .slice(0, pdfDocumentFileIdx)
        .map(key => pagesPerDocument[key].length)
        .reduce((a, b) => a + b, pageNumber),
    [pagesPerDocument, pdfDocumentFiles],
  );

  return (
    <ReactModal className="interactModal" overlayClassName="modal" isOpen>
      <div
        className="fieldShape invalid"
        ref={fieldShapeContainerRef}
        style={{
          position: 'absolute',
          zIndex: 1000,
          width: 'max-content',
          pointerEvents: 'none',
        }}
      />
      {isDocumentInteractLoading ? (
        <UISpinner width={50} height={50} wrapperClassName="spinner--main__wrapper" />
      ) : (
        <div ref={containerRef} className="interactModal__inner">
          <ModalHeader
            handleClose={onClose}
            onScaleChange={handleDocumentScaleChange}
            documentScale={selectedScale}
            handleSubmit={handleDocumentFieldsSubmit}
            isSubmitting={submitting}
            onCopy={copyDocumentField}
            onPaste={pasteDocumentField}
            submitButtonTitle={buttonSendTitle}
            isForm={currentDocument?.type === DocumentTypes.FORM_REQUEST}
            isDisableCancelButton={false}
            redoDocumentFieldAction={redoDocumentFieldAction}
            undoDocumentFieldAction={undoDocumentFieldAction}
            isNextAvailable={isNextAvailable}
            isPrevAvailable={isPrevAvailable}
          />
          <div
            className={classNames('interactModal__body', {
              template: currentDocument?.type === DocumentTypes.TEMPLATE,
            })}
          >
            <InteractFieldBar
              fieldRootRef={fieldRootRef}
              containerRef={containerRef}
              fieldShapeContainerRef={fieldShapeContainerRef}
              documentScale={documentScale}
              selectedScale={selectedScale}
              selectedFieldType={selectedFieldType}
              isDocumentFirstAndDisabled={false}
              areFieldsSelecting
              isInteractPreparing={isPreparingInteract}
              loadedPagesCount={loadedPagesCount}
              totalPagesCount={totalPagesCount}
              onFieldShapeRemove={removeFieldShape}
              setFieldShapeExist={setFieldShapeExist}
              setSelectedFieldType={setSelectedFieldType}
            />
            <div className="interactModal__documentView" ref={setDocumentViewContainer}>
              <div style={{ height: 0 }}>
                <div
                  className="interactModal__documentView-inner"
                  style={{
                    transformOrigin: `${lowScale ? 'center' : 'left'} top`,
                    width: greaterPageWidth,
                    position: lowScale ? 'relative' : 'static',
                    left: lowScale ? '50%' : undefined,
                    transform: lowScale
                      ? `translateX(-50%) scale(${documentScale})`
                      : `scale(${documentScale})`,
                  }}
                >
                  <div
                    onMouseEnter={removeInvalidityFromFieldShape}
                    onMouseLeave={addInvalidityToFieldShape}
                    ref={pagesContainerRef}
                  >
                    {pdfDocumentFiles.map((pdfDocumentFile, pdfDocumentFileIdx) => (
                      <PDFDocument
                        key={pdfDocumentFile}
                        file={pdfDocumentFile}
                        loading={<></>}
                        onLoadSuccess={proxy =>
                          handlePdfDocumentLoadSuccess(pdfDocumentFile, proxy)
                        }
                      >
                        {isAllPdfFilesLoaded &&
                          pagesPerDocument[pdfDocumentFile].map(pageNumber => (
                            <DocumentPage
                              key={pageNumber}
                              pageNumber={pageNumber}
                              className={classNames({
                                'interactModal--cursor-pointer': selectedFieldType,
                              })}
                              renderAnnotationLayer={false}
                              style={{
                                height:
                                  pdfPageMeta[
                                    getAbsolutePageNumber(pdfDocumentFileIdx, pageNumber)
                                  ].height,
                                width:
                                  pdfPageMeta[
                                    getAbsolutePageNumber(pdfDocumentFileIdx, pageNumber)
                                  ].width,
                                marginBottom: INTERACT_SIZES.pageMarginBottom,
                              }}
                              onLoad={() =>
                                handlePageRendered(
                                  getAbsolutePageNumber(pdfDocumentFileIdx, pageNumber),
                                )
                              }
                              onDrop={event =>
                                handleFieldDropOnPage(
                                  event,
                                  getAbsolutePageNumber(pdfDocumentFileIdx, pageNumber),
                                )
                              }
                              onTap={event =>
                                handlePageTap(
                                  event,
                                  getAbsolutePageNumber(pdfDocumentFileIdx, pageNumber),
                                )
                              }
                            />
                          ))}
                      </PDFDocument>
                    ))}
                  </div>
                  {documentFields
                    .filter(field => !isEmpty(field.style))
                    .map(field => (
                      <FieldItem
                        key={field.id}
                        field={field}
                        documentScale={documentScale}
                        signersOptions={signersOptions}
                        onChangeSigner={handleSignerChange}
                        disabled={isPreparingInteract}
                        onInteract={setLastInteractedFieldId}
                        onFocus={setFocusedFieldId}
                        onBlur={unsetFocusedFieldId}
                        onDocumentFieldUpdate={setFieldUpdateAction}
                        isFontResize={enableResizeFont}
                        selectedScale={selectedScale}
                        popupAvailable
                      />
                    ))}
                </div>
              </div>
            </div>
            {lastInteractedField && (
              <ul className="interactModal__fieldBar-selectField-list">
                <FieldItemOptions
                  key={lastInteractedField.id}
                  field={lastInteractedField}
                  partisipantOptions={signersOptions}
                  onUpdate={setFieldUpdateAction}
                  onChangeSigner={handleSignerChange}
                  validateTag={validateTag}
                  onDelete={deleteDocumentField}
                  onStartInputTyping={disableToDeleteLastInteractedField}
                  onStopInputTyping={enableToDeleteLastInteractedField}
                  onApplyFontToEachField={handleApplyFontToEachField}
                  showTitle={!isMedium}
                  showRemoveFieldButton={!isTablet}
                />
              </ul>
            )}
            <InteractNavigation
              documents={pdfDocumentFiles}
              pages={pagesPerDocument}
              pagesMeta={pdfPageMeta}
              totalPages={totalPagesCount}
              isLoaded={isAllPdfFilesLoaded}
              onGetFields={(documentFileIdx, pageNumber) =>
                documentFields.filter(
                  availableDocumentField =>
                    availableDocumentField.pageNumber ===
                    getAbsolutePageNumber(documentFileIdx, pageNumber),
                )
              }
              onNavigate={(documentFileIdx, pageNumber) =>
                navigateToPage(getAbsolutePageNumber(documentFileIdx, pageNumber))
              }
            />
          </div>
        </div>
      )}
    </ReactModal>
  );
}

export default InteractModalOldTablet;
